<template>
  <b-modal
    ref="importModal"
    title="Nhập Excel nhóm nhà cung cấp"
    size="xl"
    hide-footer
    no-close-on-backdrop
  >
    <div>
      <!-- Step 1: Download Template -->
      <b-row>
        <b-col>
          <div class="form-group">
            <span>
              <b>Bước 1:</b> Vui lòng chuẩn bị dữ liệu mẫu từ file excel đúng format, hoặc có thể tải mẫu ở
              <a
                href="#"
                @click.prevent="downloadTemplate"
                class="text-primary"
              >
                <b>đây</b>
              </a>
            </span>
          </div>
        </b-col>
      </b-row>

      <!-- Step 2: Upload File -->
      <b-row>
        <b-col cols="12">
          <span><b>Bước 2:</b> Tiến hành Import</span>
          <UploadFileMulter
            v-on:send-file="handleFileUpload"
            v-on:clear-file="clearFile"
            :cmdUrl="'v1/supplier-groups/import-excel'"
          />
          <span
            v-if="fileName"
            class="mt-2 d-block"
          >
            Tên file: <b>{{ fileName }}</b>
          </span>
        </b-col>
      </b-row>

      <!-- Step 3: Preview and Import -->
      <div
        v-if="importData.length > 0"
        class="mt-4"
      >
        <h5>
          <b>Bước 3:</b> Xem trước và nhập dữ liệu
        </h5>

        <!-- Summary -->
        <b-row class="mb-3">
          <b-col>
            <b-alert
              show
              variant="info"
            >
              <div class="d-flex justify-content-between">
                <span>
                  <strong>Tổng số bản ghi:</strong> {{ importData.length }}
                </span>
                <span>
                  <strong>Hợp lệ:</strong>
                  <b-badge variant="success">{{ validItems.length }}</b-badge>
                </span>
                <span>
                  <strong>Không hợp lệ:</strong>
                  <b-badge variant="danger">{{ invalidItems.length }}</b-badge>
                </span>
              </div>
            </b-alert>
          </b-col>
        </b-row>

        <!-- Preview Table -->
        <b-table
          :items="previewItems"
          :fields="previewFields"
          responsive
          bordered
          small
          class="mb-3"
          :tbody-tr-class="getRowClass"
        >
          <template v-slot:cell(status)="row">
            <b-badge :variant="row.item.isValid ? 'success' : 'danger'">
              {{ row.item.isValid ? 'Hợp lệ' : 'Không hợp lệ' }}
            </b-badge>
          </template>

          <template v-slot:cell(errors)="row">
            <div v-if="!row.item.isValid && row.item.errors">
              <small class="text-danger">
                {{ row.item.errors.join(', ') }}
              </small>
            </div>
          </template>
        </b-table>

        <!-- Show more/less button -->
        <div
          v-if="importData.length > 10"
          class="text-center mb-3"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="toggleShowAll"
          >
            {{ showAll ? 'Hiển thị ít hơn' : `Hiển thị tất cả (${importData.length} bản ghi)` }}
          </b-button>
        </div>

        <!-- Import Button -->
        <div class="d-flex justify-content-end">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="clearFile"
            :disabled="isImporting"
          >
            Hủy
          </b-button>
          <b-button
            variant="success"
            @click="handleImport"
            :disabled="validItems.length === 0 || isImporting"
          >
            <b-spinner
              small
              v-if="isImporting"
              class="mr-1"
            ></b-spinner>
            {{ isImporting ? 'Đang nhập...' : `Nhập ${validItems.length} nhóm nhà cung cấp hợp lệ` }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import UploadFileMulter from '@/view/modules/upload/UploadFileMulter.vue';
import { SupplierGroupService } from '../service/supplier-group.service';

export default {
  name: 'SupplierGroupImportModal',
  components: {
    UploadFileMulter,
  },
  data() {
    return {
      fileName: '',
      importData: [],
      isImporting: false,
      showAll: false,
      previewFields: [
        { key: 'name', label: 'Tên nhóm' },
        { key: 'code', label: 'Mã nhóm' },
        { key: 'description', label: 'Ghi chú' },
        { key: 'status', label: 'Trạng thái', class: 'text-center' },
        { key: 'errors', label: 'Lỗi' },
      ],
    };
  },
  computed: {
    validItems() {
      return this.importData.filter((item) => item.isValid);
    },
    invalidItems() {
      return this.importData.filter((item) => !item.isValid);
    },
    previewItems() {
      return this.showAll ? this.importData : this.importData.slice(0, 10);
    },
  },
  methods: {
    showModal() {
      this.$refs.importModal.show();
    },

    hideModal() {
      this.$refs.importModal.hide();
    },

    async downloadTemplate() {
      try {
        const response = await SupplierGroupService.downloadTemplate();
        const fileName = 'mau-nhap-nhom-nha-cung-cap.xlsx';

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        makeToastSuccess('Tải file mẫu thành công');
      } catch (error) {
        console.error('Download template error:', error);
        makeToastFaile('Có lỗi xảy ra khi tải file mẫu');
      }
    },

    handleFileUpload(data, fileName) {
      this.fileName = fileName;
      this.importData = data.rows || [];
      this.showAll = false;
    },

    clearFile() {
      this.fileName = '';
      this.importData = [];
      this.showAll = false;
    },

    toggleShowAll() {
      this.showAll = !this.showAll;
    },

    getRowClass(item) {
      return item.isValid ? '' : 'table-danger';
    },

    async handleImport() {
      if (this.validItems.length === 0) {
        makeToastFaile('Không có dữ liệu hợp lệ để nhập');
        return;
      }

      this.isImporting = true;

      try {
        // Prepare data for bulk create
        const supplierGroupsData = this.validItems.map((item) => ({
          name: item.name,
          code: item.code || null,
          description: item.description || null,
        }));

        const response = await SupplierGroupService.bulkCreate(
          supplierGroupsData,
        );

        if (response.data.status === 200) {
          makeToastSuccess(
            `Nhập thành công ${this.validItems.length} nhóm nhà cung cấp`,
          );
          this.hideModal();
          this.clearFile();
          this.$emit('import-success');
        } else {
          makeToastFaile(
            response.data.message || 'Có lỗi xảy ra khi nhập dữ liệu',
          );
        }
      } catch (error) {
        console.error('Import error:', error);
        const errorMessage =
          error.response?.data?.message || 'Có lỗi xảy ra khi nhập dữ liệu';
        makeToastFaile(errorMessage);
      } finally {
        this.isImporting = false;
      }
    },
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.table-danger {
  background-color: #f8d7da !important;
}
</style>
